import { Injectable, signal } from "@angular/core";
import { BroshService } from "./brosh.service";
import { AuthService } from "./auth.service";
import { ServiceOptions } from "../models/service-options";

export interface RxLockState {
  editable: boolean;
  lockedBy: string | null;
  stale: boolean;
  // Our own lock lapsed while the user was idle; activity takes it back.
  idle: boolean;
}

const RENEW_MS = 60000;
const RETAKE_MS = 15000;

// The short edit lock on a DRAFT RX: held by this browser while the user is
// inside the order, renewed every minute, released on leaving it.
@Injectable({ providedIn: "root" })
export class RxLockService {
  readonly state = signal<RxLockState | null>(null);

  private formId: number | null = null;
  private loadedUpdDate: string | null = null;
  private activity = false;
  private lastRetakeAt = -Infinity;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly broshService: BroshService,
    private readonly authService: AuthService
  ) {
    // AuthService cannot inject this service (it would be circular through
    // BroshService), so it announces the sign-out and we listen.
    this.authService.signingOut$.subscribe(() => this.stop());
    window.addEventListener("pagehide", () => this.unlockOnPageHide());
  }

  start(formId: number, loadedUpdDate: string): void {
    // Reloading the same form keeps its lock: an unlock racing the new lock
    // request could arrive after it and delete it.
    if (this.formId === formId) this.clearTimer();
    else this.stop();
    this.formId = formId;
    this.loadedUpdDate = loadedUpdDate;
    this.activity = false;
    this.lastRetakeAt = -Infinity;
    // Opening the form is activity: only activity takes a lock.
    this.lock(true);
    this.timer = setInterval(() => {
      const active = this.activity;
      this.activity = false;
      this.lock(active);
    }, RENEW_MS);
  }

  // Our own saves change upd_date; the next retake must not read that as
  // someone else's change.
  markSaved(updDate: string): void {
    if (this.formId !== null) this.loadedUpdDate = updDate;
  }

  // Activity rides on the next renewal. A lock we lost is retaken at once, at
  // most every 15 s, when a retake can work: our own lock lapsed while idle, or
  // someone else holds it (they may have left). Never for a form that changed
  // since we loaded it (stale) or one we may not edit.
  noteActivity(): void {
    if (this.formId === null) return;
    this.activity = true;
    const s = this.state();
    if (!s || s.editable || s.stale || !(s.idle || s.lockedBy != null)) return;
    const now = Date.now();
    if (now - this.lastRetakeAt < RETAKE_MS) return;
    this.lastRetakeAt = now;
    this.activity = false;
    this.lock(true);
  }

  stop(): void {
    this.clearTimer();
    const formId = this.formId;
    this.formId = null;
    this.loadedUpdDate = null;
    this.activity = false;
    this.state.set(null);
    if (formId !== null) this.broshService.unlockRxForm(formId).subscribe({ error: () => {} });
  }

  private clearTimer(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
  }

  private lock(activity: boolean): void {
    const formId = this.formId;
    if (formId === null) return;
    this.broshService.lockRxForm(formId, activity, this.loadedUpdDate).subscribe({
      next: (s) => {
        if (this.formId === formId) this.state.set(s);
      },
      error: () => {},
    });
  }

  private unlockOnPageHide(): void {
    if (this.formId === null) return;
    const params = new ServiceOptions();
    params.serviceName = "unlock_rx_form";
    params["formId"] = this.formId;
    const headers: { [k: string]: string } = {};
    const h = this.authService.getRequestHeaders();
    h.keys().forEach((k) => (headers[k] = h.get(k)!));
    fetch(this.authService.getRequestUrl("unlock_rx_form"), {
      method: "POST",
      keepalive: true,
      headers,
      body: this.authService.getRequestBody(params),
    }).catch(() => {});
  }
}
